import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '@ieEnv/environment';

interface OrgBrandingResponse {
  loginLogo?: string;
  sidebarLogo?: string;
  loginBackground?: string;
  orgName?: string;
}

function publicOrgLogoUrl(fileName: string): string {
  // Encode the filename: loginBackground is interpolated into a CSS url() on
  // the guest pages, so a crafted value must not be able to break out of it.
  return `${environment.storageHost}/storage/public/org-logos/${encodeURIComponent(
    fileName,
  )}`;
}

@Injectable({ providedIn: 'root' })
export class BrandingService {
  private loginLogoUrl$ = new BehaviorSubject<string | null>(null);
  private sidebarLogoUrl$ = new BehaviorSubject<string | null>(null);
  private loginBackgroundUrl$ = new BehaviorSubject<string | null>(null);
  private loaded = false;

  readonly loginLogo$ = this.loginLogoUrl$.asObservable();
  readonly sidebarLogo$ = this.sidebarLogoUrl$.asObservable();
  readonly loginBackground$ = this.loginBackgroundUrl$.asObservable();

  constructor(private http: HttpClient) {}

  loadBranding(): Observable<void> {
    if (this.loaded) return of(undefined);
    const domain = window.location.hostname;
    return this.http
      .get<OrgBrandingResponse>(
        `${environment.organizationHost}/orgs/public/branding`,
        { params: { domain } },
      )
      .pipe(
        tap((branding) => {
          this.loaded = true;
          if (branding.loginLogo) {
            this.loginLogoUrl$.next(publicOrgLogoUrl(branding.loginLogo));
          }
          if (branding.sidebarLogo) {
            this.sidebarLogoUrl$.next(publicOrgLogoUrl(branding.sidebarLogo));
          }
          if (branding.loginBackground) {
            this.loginBackgroundUrl$.next(
              publicOrgLogoUrl(branding.loginBackground),
            );
          }
        }),
        map(() => undefined),
        catchError((err) => {
          console.warn('[BrandingService] Failed to load branding:', err?.message || err);
          this.loaded = true;
          return of(undefined);
        }),
      );
  }
}
